import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import { capitalize } from "lodash";
import { toast } from "react-toastify";
import { playerServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { Loader } from "../../components/CustomLoader";

const FALLBACK_POSITIONS = [
  { positionId: 24, name: "Goalkeeper" },
  { positionId: 25, name: "Defender" },
  { positionId: 26, name: "Midfielder" },
  { positionId: 27, name: "Forward" },
];

export default function TeamPositionsModal({
  open,
  onClose,
  playerId,
  canEdit = false,
  onSaved,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [player, setPlayer] = useState(null);
  const [teams, setTeams] = useState([]);
  const [positions, setPositions] = useState(FALLBACK_POSITIONS);
  // playerSeasonId -> newly picked positionId (only rows the admin touched)
  const [edits, setEdits] = useState({});

  const fetchTeamPositions = useCallback(async () => {
    if (!playerId) return;
    try {
      setLoading(true);
      const res = await playerServices.getPlayerTeamPositions({ playerId });
      if (res.status === 200) {
        const payload = res?.data?.data || {};
        setPlayer(payload.player || null);
        setTeams(payload.teams || []);
        setPositions(
          payload.positions?.length ? payload.positions : FALLBACK_POSITIONS,
        );
        setEdits({});
      }
    } catch (err) {
      errorHandler(err);
      setPlayer(null);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [playerId]);

  useEffect(() => {
    if (open && playerId) {
      fetchTeamPositions();
    }
    if (!open) {
      setPlayer(null);
      setTeams([]);
      setEdits({});
    }
  }, [open, playerId, fetchTeamPositions]);

  const positionNameById = useMemo(
    () => Object.fromEntries(positions.map((p) => [String(p.positionId), p.name])),
    [positions],
  );

  const changedTeams = useMemo(
    () =>
      teams
        .filter((team) => {
          const edited = edits[team.playerSeasonId];
          return (
            edited !== undefined && Number(edited) !== Number(team.positionId)
          );
        })
        .map((team) => ({
          playerSeasonId: team.playerSeasonId,
          positionId: Number(edits[team.playerSeasonId]),
        })),
    [teams, edits],
  );

  const handlePositionChange = (playerSeasonId, value) => {
    setEdits((prev) => ({ ...prev, [playerSeasonId]: value }));
  };

  const handleSave = async () => {
    if (changedTeams.length === 0) {
      toast.info("No position changes to save.");
      return;
    }
    try {
      setSaving(true);
      const res = await playerServices.updatePlayerTeamPositions({
        playerId,
        teams: changedTeams,
      });
      if (res.status === 200) {
        toast.success(res?.data?.message || "Player positions updated");
        await fetchTeamPositions();
        if (onSaved) onSaved();
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const renderTeamRow = (team) => {
    const currentValue =
      edits[team.playerSeasonId] !== undefined
        ? edits[team.playerSeasonId]
        : (team.positionId ?? "");
    const isChanged =
      edits[team.playerSeasonId] !== undefined &&
      Number(edits[team.playerSeasonId]) !== Number(team.positionId);

    return (
      <Box
        key={team.playerSeasonId}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 1.5, sm: 2 },
          flexWrap: "wrap",
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: isChanged ? "#00a1dd" : "rgba(0, 0, 0, 0.12)",
          backgroundColor: isChanged
            ? "rgba(0, 161, 221, 0.08)"
            : "transparent",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ minWidth: { xs: 0, sm: 220 } }}
        >
          <Avatar
            src={team.teamImage}
            alt={team.teamName || "Team"}
            sx={{ width: 44, height: 44, bgcolor: "white", border: "1px solid #e0e0e0" }}
          >
            <GroupsIcon sx={{ color: "#9e9e9e" }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {team.teamName || "Unknown Team"}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
              {team.leagueLogo && (
                <Avatar
                  src={team.leagueLogo}
                  alt={team.leagueName || "League"}
                  sx={{ width: 18, height: 18 }}
                />
              )}
              <Typography variant="body2" color="text.secondary">
                {team.leagueName || "-"}
                {team.seasonName ? ` • ${team.seasonName}` : ""}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "space-between", sm: "flex-start" },
          }}
        >
          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            <Typography variant="caption" color="text.secondary">
              Current
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {team.positionName ||
                positionNameById[String(team.positionId)] ||
                "-"}
            </Typography>
          </Box>
          <Select
            size="small"
            value={currentValue === null ? "" : currentValue}
            onChange={(e) =>
              handlePositionChange(team.playerSeasonId, e.target.value)
            }
            disabled={!canEdit || saving}
            displayEmpty
            sx={{ minWidth: { xs: 150, sm: 170 } }}
          >
            <MenuItem value="" disabled>
              Select position
            </MenuItem>
            {positions.map((p) => (
              <MenuItem key={p.positionId} value={p.positionId}>
                {capitalize(p.name)}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Team Positions
          </Typography>
          <Tooltip title="Close">
            <span>
              <IconButton onClick={handleClose} disabled={saving}>
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <Loader />
          </Box>
        ) : (
          <Stack spacing={3}>
            {player && (
              <>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    src={player.imagePath}
                    alt={player.displayName || "Player"}
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: "white",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <PersonIcon sx={{ color: "#9e9e9e" }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {player.fullName || player.displayName || "-"}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Default position:
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          player.positionName ||
                          positionNameById[String(player.positionId)] ||
                          "-"
                        }
                      />
                    </Stack>
                  </Box>
                </Stack>
                <Divider />
              </>
            )}

            {teams.length === 0 ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <GroupsIcon sx={{ fontSize: 48, color: "#bdbdbd" }} />
                <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                  No current-season teams
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This player is not part of any team in an active season.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>{teams.map(renderTeamRow)}</Stack>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {changedTeams.length > 0
            ? `${changedTeams.length} unsaved change${changedTeams.length !== 1 ? "s" : ""}`
            : ""}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          {canEdit && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || loading || changedTeams.length === 0}
            >
              {saving ? "Saving..." : "Save changes"}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
